import type { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import useHasAccessToTowPro from '@rsos/hooks/useHasAccessToTowPro';
import {
  digitalDispatchApi,
  DIGITAL_DISPATCH_EVENT_TYPES,
} from '@rsos/reduxDataStore/src/features/resourceRequests/digitalDispatchApi';

const DIGITAL_DISPATCH_EVENTS = new Set<string>(
  Object.values(DIGITAL_DISPATCH_EVENT_TYPES),
);

/**
 * Subscribes to StreamWS and invalidates the digital dispatch RTK Query cache
 * when digital dispatch websocket events arrive, so tow request rows update
 * without waiting for the next poll. Matches other streamWS_hooks patterns.
 */
const useHandleDigitalDispatchMessages = (): void => {
  const StreamWS = window?.__RSOS?.streamWS?.instance;
  const dispatch =
    useDispatch<ThunkDispatch<unknown, unknown, UnknownAction>>();
  const enabled = useHasAccessToTowPro();

  const invalidateRequests = useCallback(() => {
    dispatch(
      digitalDispatchApi.util.invalidateTags(['DigitalDispatchRequests']),
    );
  }, [dispatch]);

  const handleDigitalDispatchMessage = useCallback(
    (message: Record<string, unknown>) => {
      if (DIGITAL_DISPATCH_EVENTS.has(message?.type as string)) {
        invalidateRequests();
      }
    },
    [invalidateRequests],
  );

  useEffect(() => {
    if (!StreamWS || !enabled) {
      return;
    }

    StreamWS.addOnMessageCallback({
      handleDigitalDispatchMessage,
    });
    StreamWS.addOnReconnectCallback({
      handleDigitalDispatchReconnect: invalidateRequests,
    });

    return () => {
      StreamWS.removeOnMessageCallback('handleDigitalDispatchMessage');
      StreamWS.removeOnReconnectCallback('handleDigitalDispatchReconnect');
    };
  }, [StreamWS, handleDigitalDispatchMessage, invalidateRequests, enabled]);
};

export default useHandleDigitalDispatchMessages;
